import type { ComponentProps, ReactNode } from 'react';

import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { cn } from '@repo/lib/cn';
import { CheckIcon, MinusIcon } from 'lucide-react';

import { Label } from '@/components/ui/label';

export function Checkbox(checkboxProps: ComponentProps<typeof CheckboxPrimitive.Root>) {
  const { className, ...props } = checkboxProps;
  return (
    <CheckboxPrimitive.Root
      data-slot="checkbox"
      className={cn(
        'group/checkbox peer shadow-xs border-border-primary bg-input dark:bg-input/35 hover:bg-accent aria-checked:border-border-brand aria-checked:bg-brand dark:aria-checked:bg-brand/75 aria-checked:text-brand-foreground transition-outline focus-visible:bg-input-focus dark:focus-visible:bg-input-focus/25 outline-focus-ring size-5 shrink-0 cursor-pointer rounded-sm border opacity-100 outline-0 transition-colors duration-150 focus-visible:outline-3 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-60',
        className
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator className={cn('flex items-center justify-center text-current')}>
        <CheckIcon className="pointer-events-none hidden size-3.5 shrink-0 group-data-[state=checked]/checkbox:block" />
        <MinusIcon className="pointer-events-none hidden size-3.5 shrink-0 group-data-[state=indeterminate]/checkbox:block" />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}

interface CheckboxWithLabelProps extends ComponentProps<typeof Checkbox> {
  label: string;
  description?: string | ReactNode;
}

export function CheckboxWithLabel(checkboxWithLabelProps: CheckboxWithLabelProps) {
  const { className, id, label, description } = checkboxWithLabelProps;
  return (
    <div className={`flex items-start gap-2 ${className}`}>
      <Checkbox id={id} aria-describedby={description ? `${id}-description` : undefined} />
      <div className="grid grow gap-1">
        <Label htmlFor={id}>{label}</Label>
        {description && (
          <p id={`${id}-description`} className="text-muted-foreground text-xs">
            {description}
          </p>
        )}
      </div>
    </div>
  );
}
